/**
 * The w2b-disclosure item delta (paio-commerce `b720788`), typed at the edge
 * (ORDR-121). Endpoint #7 item keys, `snake_case` as sent:
 *
 *   allergens:     [{allergen, state}] | null     never []
 *   dietary_tags:  [code]                         [] is NO CLAIM
 *   restricted:    [{class, state}] | null        never []
 *   availability:  {status, unavailable_until}
 *
 * Each key reads as `recorded` (a list), `not-recorded` (`null`, where the
 * server may send it) or `malformed` (anything else, including a `[]` the
 * server never sends). Nothing here throws, and nothing here decides what a
 * state MEANS: an unknown code or state is carried through as the string it
 * was, so the renderer can show it rather than drop it.
 */

export interface WireAllergen {
  readonly allergen: string;
  readonly state: string;
}

export interface WireRestricted {
  readonly class: string;
  readonly state: string;
}

export interface WireAvailability {
  readonly status: string;
  readonly unavailable_until: string | null;
}

export type Disclosed<T> =
  | { readonly kind: "recorded"; readonly value: readonly T[] }
  | { readonly kind: "not-recorded" }
  | { readonly kind: "malformed" };

/** `absent` is an older commerce that does not send the key at all. */
export type AvailabilityRead =
  | { readonly kind: "recorded"; readonly value: WireAvailability }
  | { readonly kind: "absent" }
  | { readonly kind: "malformed" };

export interface WireDisclosure {
  readonly allergens: Disclosed<WireAllergen>;
  readonly dietaryTags: Disclosed<string>;
  readonly restricted: Disclosed<WireRestricted>;
  readonly availability: AvailabilityRead;
}

const MALFORMED = { kind: "malformed" } as const;

function record(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/** A non-empty list of `{<key>: string, state: string}`, or `null`. */
function stateList<T>(value: unknown, key: string, build: (name: string, state: string) => T): Disclosed<T> {
  if (value === null) {
    return { kind: "not-recorded" };
  }
  if (!Array.isArray(value) || value.length === 0) {
    return MALFORMED;
  }
  const entries: T[] = [];
  for (const raw of value) {
    const entry = record(raw);
    const name = entry?.[key];
    const state = entry?.["state"];
    if (typeof name !== "string" || typeof state !== "string") {
      return MALFORMED;
    }
    entries.push(build(name, state));
  }
  return { kind: "recorded", value: entries };
}

function tags(value: unknown): Disclosed<string> {
  return Array.isArray(value) && value.every((tag) => typeof tag === "string")
    ? { kind: "recorded", value: value as string[] }
    : MALFORMED;
}

function availability(value: unknown): AvailabilityRead {
  if (value === undefined) {
    return { kind: "absent" };
  }
  const entry = record(value);
  const status = entry?.["status"];
  const until = entry?.["unavailable_until"];
  if (typeof status !== "string" || (typeof until !== "string" && until !== null && until !== undefined)) {
    return MALFORMED;
  }
  return { kind: "recorded", value: { status, unavailable_until: typeof until === "string" ? until : null } };
}

/** The disclosure keys of one wire item. A non-record reads as all malformed. */
export function readWireDisclosure(raw: unknown): WireDisclosure {
  const item = record(raw);
  if (item === null) {
    return { allergens: MALFORMED, dietaryTags: MALFORMED, restricted: MALFORMED, availability: MALFORMED };
  }
  return {
    allergens: stateList(item["allergens"], "allergen", (allergen, state) => ({ allergen, state })),
    dietaryTags: tags(item["dietary_tags"]),
    restricted: stateList(item["restricted"], "class", (name, state) => ({ class: name, state })),
    availability: availability(item["availability"]),
  };
}
